/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import AppearanceControlsButton from '../appearance-controls/button';
import type { ColorMode, FeatureFlagSwitcherConfig, SectionIds } from '../types';
import CodeSandbox, { type CodeSandboxProps } from './code-sandbox';
import Copy from './copy';

const styles = cssMap({
	root: {
		display: 'grid',
		backgroundColor: token('elevation.surface.sunken'),
	},
	rootShowcaseVisible: {
		gridTemplateColumns: 'min-content auto',
	},
	rootShowcaseHidden: {
		gridTemplateColumns: 'auto',
		borderStartStartRadius: token('radius.small'),
		borderStartEndRadius: token('radius.small'),
	},
	rootSourceCodeVisible: {
		borderBlockEndColor: token('color.border'),
		borderBlockEndWidth: token('border.width'),
		borderBlockEndStyle: 'solid',
	},
	rootSourceCodeHidden: {
		borderEndStartRadius: token('radius.small'),
		borderEndEndRadius: token('radius.small'),
	},
	section: {
		paddingBlock: token('space.050'),
		paddingInline: token('space.150'),
	},
	appearanceControls: {
		borderInlineEndColor: token('color.border'),
		borderInlineEndWidth: token('border.width'),
		borderInlineEndStyle: 'solid',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	buttonGroup: {
		display: 'flex',
		flexWrap: 'wrap',
		justifyContent: 'flex-end',
		gap: token('space.050'),
	},
});

type ExampleActionsProps = {
	isShowcaseVisible: boolean;
	isSourceCodeVisible: boolean;
	isCodeSandboxDisabled?: boolean;
	codeSandboxInfo?: CodeSandboxProps;
	sourceCode: string;
	appearanceControls: {
		colorMode?: ColorMode;
		onColorModeChange?(colorMode: ColorMode): void;
		hiddenSections?: SectionIds;
		featureFlagSwitchers?: FeatureFlagSwitcherConfig[];
	};
	testId?: string;
};

/**
 * __Example actions__.
 *
 * Example actions sit within an example displays various actions for the user.
 *
 */
export const ExampleActions: ({
	isShowcaseVisible,
	isSourceCodeVisible,
	isCodeSandboxDisabled,
	codeSandboxInfo,
	sourceCode,
	appearanceControls,
	testId,
}: ExampleActionsProps) => JSX.Element = ({
	isShowcaseVisible,
	isSourceCodeVisible,
	isCodeSandboxDisabled,
	codeSandboxInfo,
	sourceCode,
	appearanceControls,
	testId,
}: ExampleActionsProps) => {
	return (
		<div
			css={[
				styles.root,
				isShowcaseVisible ? styles.rootShowcaseVisible : styles.rootShowcaseHidden,
				isSourceCodeVisible ? styles.rootSourceCodeVisible : styles.rootSourceCodeHidden,
			]}
		>
			{isShowcaseVisible && (
				<div css={[styles.appearanceControls, styles.section]}>
					<AppearanceControlsButton
						colorMode={appearanceControls.colorMode}
						onColorModeChange={appearanceControls.onColorModeChange}
						popupPlacement="bottom-start"
						testId={testId ? `${testId}--appearance-controls` : undefined}
						hiddenSections={appearanceControls.hiddenSections}
						featureFlagSwitchers={appearanceControls.featureFlagSwitchers}
					/>
				</div>
			)}
			<div css={[styles.section, styles.buttonGroup]}>
				<Copy source={sourceCode} />
				{!isCodeSandboxDisabled && codeSandboxInfo && <CodeSandbox {...codeSandboxInfo} />}
			</div>
		</div>
	);
};
